import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CUSTOM_VALUES, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../../constants/command-status";
import { FILTER_COMPANIES, FILTER_DEPARTMENTS, FILTER_DOORS, FILTER_PROFILE, FILTER_SITES } from "../../../constants/filter-types";
import { DEF_PG_SIZE_TVIEWER } from "../../../constants/maximum-page-size";
import { MONITORING_DASHBOARD } from "../../../constants/menu-types";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { useIcaWebsocketContext } from "../../../contexts/websocket/IcaWebsocketContext";
import accessBellIcon from '../../../images/AccessBell.svg';
import alarmBellIcon from '../../../images/AlarmBell.svg';
import deniedBellIcon from '../../../images/DeniedBell.svg';
import PauseIcon from '../../../images/Pause - Icon + Circle.svg';
import PlayIcon from '../../../images/Play - Icon + Circle.svg';
import statusBellIcon from '../../../images/StatusBell.svg';
import { Category } from "../../../models/Category";
import LiveTransactionsWidgetColumns from "../../../models/ColumnDefinitions/LiveTransactionsWidgetColumns";
import { Filter } from "../../../models/Filter";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { TransactionViewer } from "../../../models/TransactionViewer";
import { Widget } from "../../../models/Widget";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import DeviceImproxSearch from "../../../models/search/DeviceImproxSearch";
import DoorSearch from "../../../models/search/DoorSearch";
import EventTypeSearch from "../../../models/search/EventTypeSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import TransactionViewerSearch from "../../../models/search/TransactionViewerSearch";
import ZoneSearch from "../../../models/search/ZoneSearch";
import { TableLegendItem } from "../../../models/table/TableLegendItem";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getDeviceImproxNames } from "../../../services/deviceImproxService";
import { getDoorNamesWithPagination } from "../../../services/doorService";
import { getEventTypeNamesWithPagination } from "../../../services/eventTypeService";
import { getAllEnrolmentProfileNames } from "../../../services/profileService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { showError } from "../../../services/utilities/ServiceError";
import { getZoneNamesWithPagination } from "../../../services/zoneService";
import { IAppState } from "../../../store/types";
import { showTagCode } from "../../../utilities/Permissions";
import TableLegend from "../../table/table-legend/table-legend";
import BaseWidget from "../base-widget";
import DashboardTable from "../dashboard-table";
import TransactionQuickEnrollModal from "../quick-enrol/transaction-quick-enrol";

interface LiveTransactionWidgetProps {
    widget: Widget
}

const LiveTransactionWidget: FC<LiveTransactionWidgetProps> = ({ widget }) => {
    const { translate } = useLocale();
    // TODO find a better way to track row changes, we probably might have to add a fix to the filters
    let hasMounted = false;
    const [rows, setRows] = useState<TransactionViewer[]>([])
    let oldRows: TransactionViewer[] = [];
    const user = useSelector((state: IAppState) => (state.userInfo));
    const eventStatusFilterKey: string = 'status';
    const [openQuickEnrol, setOpenQuickEnrol] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionViewer | null>(null);

    const [categories, setCategories] = useState<Category[]>([{
        displayName: translate.byKey(TranslationKeys.transactions),
        heading: translate.byKey(TranslationKeys.transactions),
        value: "",
        id: 0,
        count: 0,
    }]);

    const eventCategories = {
        access: { value: 'allowed', id: "1" },
        denied: { value: 'denied', id: "2" },
        alarm: { value: 'alarm', id: "3" },
        status: { value: 'status', id: "4" },
    };

    const [eventLegend] = useState<TableLegendItem[]>([{
        value: eventCategories.access.id,
        imageIcon: accessBellIcon,
        label: translate.byKey(TranslationKeys.access),
        tooltip: '',
        isSelected: true
    }, {
        value: eventCategories.denied.id,
        imageIcon: deniedBellIcon,
        label: translate.byKey(TranslationKeys.denied),
        tooltip: '',
        isSelected: true
    }, {
        value: eventCategories.alarm.id,
        imageIcon: alarmBellIcon,
        label: translate.byKey(TranslationKeys.alarm),
        tooltip: '',
        isSelected: true
    }, {
        value: eventCategories.status.id,
        imageIcon: statusBellIcon,
        label: translate.byKey(TranslationKeys.status),
        tooltip: '',
        isSelected: true
    }]);

    const icaWebSocket = useIcaWebsocketContext();
    const [broadcastTransaction, setBroadcastTransaction] = useState<boolean>(false);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const lookup: Lookup[] = [
        {
            name: 'site',
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'zone',
            values: [],
            dataMethod: getZoneNamesWithPagination,
            searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'category',
            values: [],
            dataMethod: getAllEnrolmentProfileNames,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'device',
            values: [],
            dataMethod: getDeviceImproxNames,
            searchAccessor: DeviceImproxSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'event',
            values: [],
            dataMethod: getEventTypeNamesWithPagination,
            searchAccessor: EventTypeSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "company",
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "department",
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'door',
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessageReceived);
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

        useEffect(() => {
            dataRefresh((listParameters?: ListQuery) => getTransactions(listParameters));
    }, [rows])

    const getTransactions =(listParameters?: ListQuery): Promise<ListData> => {

        let modifiedRows = rows.filter(item => item[listParameters?.searchTerm?.accessor ?? 'event'].toUpperCase().includes((listParameters?.searchTerm?.value ?? '').toUpperCase()));
        if (hasMounted === false && modifiedRows.length === 0 ) { // this is a hack to get around the fact that the filters are applied before we even need them
            modifiedRows = oldRows;
            hasMounted = true;
        }

        modifiedRows = modifiedRows.length === 0 ? rows : modifiedRows;
        const allFilters = [...listParameters?.filters ?? []];
        const selectedFilter: { key: string, selectedOptions: string[] }[] | undefined = allFilters.map(filter => {
            const filterNotBeingIgnored: MultiSelectOption[] = filter.selected?.filter(selected => selected.ignore !== true);

            if (filterNotBeingIgnored && filterNotBeingIgnored.length > 0) {
                return {
                    key: filter.key, selectedOptions: filterNotBeingIgnored.map(selectedFilter => {
                        return selectedFilter.value
                    })
                }

            }

            return undefined

        }).filter(filter => filter !== undefined) as { key: string, selectedOptions: string[] }[];

        const realModifiedRows = modifiedRows.filter(row => {
            let isMatch: boolean = true;
            selectedFilter.forEach(filter => {
                isMatch = filter.selectedOptions.includes(row[filter.key].toString()) && isMatch
            })

            return isMatch;
        })
        // Show latest transactions top of the list
        realModifiedRows.sort((a: any, b: any) => {
            return b.dateTime > a.dateTime ? 1 : -1;
        });

        return new Promise((resolve, reject) => {
            return resolve({
                rows: realModifiedRows,
                pagination: { maxPageSize: DEF_PG_SIZE_TVIEWER, pageNumber: 1, totalCount: modifiedRows.length, totalPages: 1 }
            });
        })

    }

    const handleRowClick = (transaction: TransactionViewer) => {
        if (transaction.eventCategory === eventCategories.denied.value) {
            setSelectedTransaction(transaction);
            setOpenQuickEnrol(true);
        }
    };

    const onBroadcastChange = () => {
        setBroadcastTransaction(!broadcastTransaction);

        if (!broadcastTransaction) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
        else {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START, CUSTOM_VALUES.NO_REFRESH);
        }
    }

    const onMessageReceived = (data) => {
        try {
            const mapEventCategoryToStatus = eventCategory => {
                return Object.values(eventCategories).find(item => eventCategory === item.value)?.id ?? "0";
            };

            const response = JSON.parse(data.data);
            if (response.command === MONITORING_COMMAND.MONITORING_TRANSACTIONS) {

                response.events.forEach((event) => {
                    const dateTime = event.attributes.timestampLocal.replace("T", " ").replace(".000Z", "").replace(".000", "").substring(0, 19);
                    const eventId = event.attributes.subject.event.key.eventTypeId;
                    const device = event.attributes.source.device.attributes.name;
                    const transaction: TransactionViewer = {
                        id: event.attributes.id,
                        name: event.attributes.subject.event.attributes.nameOriginal,
                        tagCode: event.attributes.subject.credential ? showTagCode(user.profileId, event.attributes.subject.credential.attributes.tagcode) : '-',
                        dateTime: dateTime,
                        event: event.attributes.subject.event.attributes.nameOriginal,
                        eventId: eventId,
                        eventCategory: event.attributes.subject.event.attributes.eventCategory.toLowerCase(),
                        device: device,
                        site: event.attributes.subject.site.attributes.siteName,
                        credentialHolder: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.mstDisplayname : '-',
                        deviceId: event.attributes.source.device.key.deviceId,
                        siteId: event.attributes.subject.site.attributes.siteId,
                        door: event.attributes.subject.door ? event.attributes.subject.door.attributes.doorName : '-',
                        doorId: event.attributes.subject.door ? event.attributes.subject.door.attributes.doorId : '-',
                        zone: event.attributes.subject.zone ? event.attributes.subject.zone.attributes.zoneName : '-',
                        zoneId: event.attributes.subject.zone ? event.attributes.subject.zone.attributes.zoneId : '-',
                        category: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.enrolmentCategory.attributes.name : '-',
                        categoryId: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.enrolmentCategory.key.profileId : '-',
                        status: mapEventCategoryToStatus(event.attributes.subject.event.attributes.eventCategory.toLowerCase()).toString(),
                        credentialHolderId: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.key.masterId : '-',
                        reader: event.attributes.subject.reader ? event.attributes.subject.reader.attributes.name : '-',
                        controller: event.attributes.subject.controller ? event.attributes.subject.controller.attributes.name : '-',
                        companyId: event?.attributes?.subject?.company?.attributes.companyId || '-',
                        departmentId: event?.attributes?.subject?.department?.attributes.departmentId || '-'
                    }

                    const eventAlreadyExists = oldRows.some(row => row.id === transaction.id);

                    if (eventAlreadyExists) {
                        return;
                    }
                    oldRows.unshift(transaction);
                    // trim of the last element if transaction rows are more than 50
                    if (oldRows.length > 50) {
                        oldRows.pop();
                    }
                })

                setCategories([{ ...categories[0], count: oldRows.length }]);
                setRows([...oldRows]);
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }



    const onEdit = (key: string) => {

    }


    const togglePlayTransactions = (): JSX.Element => {
        return <button onClick={onBroadcastChange} className={`flex rounded-lg items-center`} id="togglePlayPauseButton">
            <span className="mr-2 text-sm font-bold">{broadcastTransaction ? translate.byKey(TranslationKeys.play) : translate.byKey(TranslationKeys.pause)}</span>
            <img
                src={broadcastTransaction ? PlayIcon : PauseIcon}
                alt="Transaction Status Button" />
        </button>
    }

    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys, settingId) => void) => {
        const staticFilterKeys = ["siteId", 'categoryId', 'departmentId', 'companyId', 'doorId'];
        const currentMenuDetail = MONITORING_DASHBOARD;

        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail).then((response) => {
            const siteFilter = response.find(filter => filter.filterId === FILTER_SITES);
            const doorFilter = response.find(filter => filter.filterId === FILTER_DOORS);
            const departmentFilter = response.find(filter => filter.filterId === FILTER_DEPARTMENTS);
            const companyFilter = response.find(filter => filter.filterId === FILTER_COMPANIES);
            const profileFilter = response.find(filter => filter.filterId === FILTER_PROFILE);

            if (!!!siteFilter) {
                customFilter.push(
                    {
                        isCustomFilter: true,
                        key: "siteId",
                        label: translate.byKey(TranslationKeys.site),
                        options: [],
                        selected: [{
                            label: translate.byKey(TranslationKeys.all),
                            value: "*",
                            ignore: true
                        }],
                        isStatic: true,
                    }
                );
            }

            if (!!!doorFilter) {
                customFilter.push(
                    {
                        isCustomFilter: true,
                        key: "doorId",
                        label: translate.byKey(TranslationKeys.door),
                        options: [],
                        selected: [{
                            label: translate.byKey(TranslationKeys.all),
                            value: "*",
                            ignore: true
                        }],
                        isStatic: true,
                    }
                );
            }

            if (!!!departmentFilter) {
                customFilter.push(
                    {
                        isCustomFilter: true,
                        key: "departmentId",
                        label: translate.byKey(TranslationKeys.department),
                        options: [],
                        selected: [{
                            label: translate.byKey(TranslationKeys.all),
                            value: "*",
                            ignore: true
                        }],
                        isStatic: true,
                    }
                );
            }

            if (!!!companyFilter) {
                customFilter.push(
                    {
                        isCustomFilter: true,
                        key: "companyId",
                        label: translate.byKey(TranslationKeys.company),
                        options: [],
                        selected: [{
                            label: translate.byKey(TranslationKeys.all),
                            value: "*",
                            ignore: true
                        }],
                        isStatic: true,
                    }
                );
            }

            if (!!!profileFilter) {
                customFilter.push(
                    {
                        isCustomFilter: true,
                        key: "categoryId",
                        label: translate.byKey(TranslationKeys.category),
                        options: [],
                        selected: [{
                            label: translate.byKey(TranslationKeys.all),
                            value: "*",
                            ignore: true
                        }],
                        isStatic: true,
                    }
                );
            }
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, undefined, staticFilterKeys, widget.id);
        })
    }

    return <BaseWidget
        widgetKey={widget.id.toString()}
        onEdit={onEdit}
        title={translate.byKey(TranslationKeys.liveTransactions)}
        titleBackgroundColor={widget.titleBackgroundColor}
        titleForegroundColor={widget.titleForegroundColor}>
        <div className="px-5 pt-3 pb-8 my-1 liveTransactionWidget" style={{ height: '460px' }}>
            <TableContextProvider
                dataMethod={getTransactions}
                tableDescriptor={LiveTransactionsWidgetColumns}
                searchDescriptor={TransactionViewerSearch}
                legendItems={eventLegend}
                legendKey={eventStatusFilterKey}
                legendLabel={'Transaction Type'}
                useTable
                useFilter
                useSearch
                useLegend
                manualDataRefresh={manualDataRefresh}
                initialiser={initialiseUserPreferences}
                lookup={lookup}>
                <DashboardTable useTableLegend={false} onRowClicked={handleRowClick}>
                    <div className="ml-auto">
                        {togglePlayTransactions()}
                    </div>
                </DashboardTable>
                <TableLegend />

                <TransactionQuickEnrollModal
                    transaction={selectedTransaction}
                    isOpen={openQuickEnrol}
                    onClose={() => setOpenQuickEnrol(false)}
                />
            </TableContextProvider>
        </div>
    </BaseWidget>
}

export default LiveTransactionWidget;
